import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building, Globe, Mail, Pencil, Shield, Upload } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/axios';
import { toast } from 'sonner';
import { resolveMediaUrl } from '../lib/mediaUrl';

const labelClass =
  'block text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5 ml-1';
const valueBoxClass =
  'text-sm px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-100';

const SuperAdminTenantView = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        setLoading(true);
        try {
          const res = await api.get(`/tenants/${tenantId}`);
          const data = res.data?.data;
          if (!data) throw new Error('Tenant not found');
          setTenant(data);
        } catch (singleErr) {
          // Fallback for environments where /tenants/:id is unavailable or stale backend process is running.
          if (singleErr?.response?.status === 404) {
            const listRes = await api.get('/tenants');
            const list = Array.isArray(listRes.data?.data) ? listRes.data.data : [];
            const matched = list.find((t) => String(t.id) === String(tenantId));
            if (matched) {
              setTenant(matched);
              return;
            }
            throw new Error('Tenant not found');
          }
          throw singleErr;
        }
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || 'Failed to load tenant');
        navigate('/super-admin/tenants');
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [navigate, tenantId]);

  if (loading || !tenant) {
    return (
      <DashboardLayout userRole="super_admin">
        <div className="w-full min-h-0 bg-slate-50 dark:bg-slate-900">
          <div className="w-full max-w-none mx-auto">
            <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 py-10 px-4 text-center text-sm text-slate-500 dark:text-slate-400">
              Loading tenant...
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const domains = Array.isArray(tenant.allowed_domains) ? tenant.allowed_domains : [];

  return (
    <DashboardLayout userRole="super_admin">
      <div className="w-full min-h-0 bg-slate-50 dark:bg-slate-900 pb-2">
        <div className="w-full max-w-none mx-auto space-y-6">
          <div className="rounded-2xl tenant-brand-gradient text-white p-5 shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <button
                  type="button"
                  onClick={() => navigate('/super-admin/tenants')}
                  className="w-10 h-10 shrink-0 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/30 transition-all active:scale-95"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0">
                  <h1 className="text-xl font-bold tracking-tight truncate">{tenant.name}</h1>
                  <p className="text-white/80 text-xs sm:text-sm">Read-only tenant details</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/super-admin/tenants/${tenantId}/edit`)}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-900 font-bold text-sm shadow-sm hover:bg-slate-50 transition-all active:scale-95"
              >
                <Pencil className="w-4 h-4" />
                Edit tenant
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden p-6 sm:p-8 space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <Building className="w-4 h-4 text-indigo-500" />
                  Identity & branding
                </h3>
                <div>
                  <span className={labelClass}>Company name</span>
                  <div className={valueBoxClass}>{tenant.name || '—'}</div>
                </div>
                <div>
                  <span className={labelClass}>URL slug</span>
                  <div className={`${valueBoxClass} font-mono text-indigo-600 dark:text-indigo-400`}>
                    {tenant.slug || '—'}
                  </div>
                </div>
                <div>
                  <span className={labelClass}>Portal display name</span>
                  <div className={valueBoxClass}>{tenant.portal_name || '—'}</div>
                </div>
                <div>
                  <span className={labelClass}>Support email</span>
                  <div className={`${valueBoxClass} flex items-center gap-2`}>
                    <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                    {tenant.support_email || '—'}
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-500" />
                  Status & appearance
                </h3>
                <div>
                  <span className={labelClass}>Status</span>
                  <div className={valueBoxClass}>
                    {tenant.is_active ? (
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Active</span>
                    ) : (
                      <span className="text-slate-500 font-semibold">Inactive</span>
                    )}
                  </div>
                </div>
                <div>
                  <span className={labelClass}>Brand colors</span>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                      <span
                        className="w-8 h-8 rounded border border-slate-200 dark:border-slate-600"
                        style={{ backgroundColor: tenant.primary_color || '#4f46e5' }}
                      />
                      <span className="text-xs font-mono text-slate-600 dark:text-slate-300 uppercase">
                        {tenant.primary_color || '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                      <span
                        className="w-8 h-8 rounded border border-slate-200 dark:border-slate-600"
                        style={{ backgroundColor: tenant.accent_color || '#a855f7' }}
                      />
                      <span className="text-xs font-mono text-slate-600 dark:text-slate-300 uppercase">
                        {tenant.accent_color || '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <span className={labelClass}>Logo</span>
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/50">
                    <div className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                      {tenant.logo_url ? (
                        <img src={resolveMediaUrl(tenant.logo_url)} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <Upload className="text-slate-300 w-6 h-6" />
                      )}
                    </div>
                    <span className="text-xs text-slate-500 break-all">{tenant.logo_url || 'No logo URL'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 dark:border-slate-700 pt-8">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 mb-4">
                <Globe className="w-4 h-4 text-violet-500" />
                Allowed domains ({domains.length})
              </h3>
              {domains.length === 0 ? (
                <p className="text-sm text-slate-500">No domains configured. Add them from the tenant list (domains) or edit tenant.</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {domains.map((d) => (
                    <li
                      key={d.domain || d.id || String(d)}
                      className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700/50 text-sm font-mono text-slate-700 dark:text-slate-200"
                    >
                      {typeof d === 'string' ? d : d.domain}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminTenantView;
